import React, { useContext, useMemo } from 'react';
import * as R from 'ramda';
import { useTranslation } from 'react-i18next';
import TitleContainer from '../TitleContainer/TitleContainer';
import Container from '../Container/Container';
import SVGInfo from '../../assets/svg/icons/SVGInfo';
import ButtonContainer from '../Button/ButtonContainer';
import Button, { ButtonStyle } from '../Button/Button';
import { ErrorType } from '../../../utils/errors';
import formatDate from '../../../utils/date';
import { GatekeeperNetworkServiceContext } from '../../../providers/GatekeeperNetworkService/GatekeeperNetworkServiceContext';
import stringUtils from '../../../common/stringUtils';

// TODO also look for 'requested' when the portal allows user to 'burn' a requested record
const matchesContainsActiveOrRequested = (matches: string[]): string | undefined => {
  return matches.find((match) => match.toLowerCase().includes('active') || match.toLowerCase().includes('requested'));
}
interface Props {
  revocationPortalUrl: string;
  matches: string[];
  onConfirm: () => void;
  errorType: ErrorType;
  revocationTimestamp?: number;
}

const UniquenessError: React.FC<Props> = ({
  revocationPortalUrl,
  matches,
  onConfirm,
  errorType,
  revocationTimestamp,
}): JSX.Element => {
  const { t } = useTranslation();

  const { burnCooldownPeriodMinutes, burnCooldownPeriodLabel } = useContext(GatekeeperNetworkServiceContext);

  const eligibleAfter = useMemo(() => {
    if (revocationTimestamp) {
      const revokedOn = new Date(revocationTimestamp * 1000);
      const eligibleAfterDate = new Date(revokedOn.getTime() + burnCooldownPeriodMinutes * 60000);
      return formatDate(eligibleAfterDate);
    }
    return '';
  }, [revocationTimestamp]);

  return (
    <Container>
      <>
        {errorType === ErrorType.NotUnique && (
          <>
            <TitleContainer center>
              <h1 className="mb-2" data-testid="UNIQUENESS_ERROR_SCREEN">
                {t('screens.uniquenessError.title')}
              </h1>
            </TitleContainer>
            <>
              <div className="px-8 pass-status-text">
                <p className="mb-2">{t('screens.uniquenessError.description')}</p>
                <p>{t('screens.uniquenessError.reconnect')}</p>
              </div>
            </>
            {matches.length > 0 && (
              <div className="w-full flex-col items-center mt-2 pr-4 pl-2 pt-4 border border-border rounded-md">
                {R.uniq(matches).map((match) => {
                  return (
                    <div className="flex mb-4" key={match}>
                      <div className="mx-2 flex items-center">
                        <SVGInfo />
                      </div>
                      <div className="break-all">
                        <p>
                          {t('screens.uniquenessError.wallet')}: {stringUtils.stripDidPreamble(match)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {matchesContainsActiveOrRequested(matches) && (<a
              data-testid="REVOKE_UNIQUENESS_HYPERLINK"
              className="mt-4 textLink"
              href={revocationPortalUrl}
              target="_blank"
              rel="noreferrer"
            >
              {t('screens.uniquenessError.removePass')}
            </a>)}
          </>
        )}
        {errorType === ErrorType.RevocationTooRecent && (
          <>
            <TitleContainer center>
              <h1 className="mb-2" data-testid="UNIQUENESS_ERROR_SCREEN">
                {t('screens.revocationTooRecentError.title')}
              </h1>
            </TitleContainer>
            {burnCooldownPeriodLabel && (
              <div className="w-full flex-col items-center text-center">
                <p>{t('screens.revocationTooRecentError.description', { burnCooldownPeriodLabel })}</p>
              </div>
            )}
            &nbsp;
            {eligibleAfter && <p>{t('screens.revocationTooRecentError.eligibleAfter', { time: eligibleAfter })}</p>}
          </>
        )}
      </>

      <ButtonContainer bottomMargin="my-4">
        <a
          data-testid="GET_HELP_HYPERLINK"
          className="mb-4 textLink"
          href="https://support.civic.com/"
          target="_blank"
          rel="noreferrer"
        >
          {t('screens.uniquenessError.getHelp')}
        </a>
        <Button style={ButtonStyle.DEFAULT} disabled={false} onClick={onConfirm} dataTestid="OK_BUTTON">
          <span>{t('screens.uniquenessError.buttonLabel')}</span>
        </Button>
      </ButtonContainer>
    </Container>
  );
};

export default UniquenessError;
